import { EditingTabInfo } from "common/types/EditingTabInfo";
import { LockStatus } from "common/types/LockStatus";
import { useState, ReactNode, createContext, Dispatch, SetStateAction } from "react";

type StatusContextType = {
  currentDate: Date;
  selectDate: (date: Date) => void;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  loadingMessage: string;
  setLoadingMessage: Dispatch<SetStateAction<string>>;
  errorMessage: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  toastTitle: string;
  toastMessage: string;
  toastVariant: string;
  setToastMessage: Dispatch<SetStateAction<string>>;
  showToastMessage: (title: string, msg: string, variant?: string) => void;
  isEditing: boolean; // Tab ID
  editingTab: EditingTabInfo | undefined;
  setEditingTab: (tabInfo: EditingTabInfo | undefined) => void;
  sideBarOpen: boolean;
  setSideBarOpen: Dispatch<SetStateAction<boolean>>;
  lockStatus: LockStatus | undefined;
  setLockStatus: Dispatch<SetStateAction<LockStatus | undefined>>;
};

const StatusContext = createContext({
  currentDate: new Date(),
  isLoading: false,
  loadingMessage: "",
  errorMessage: "",
  toastMessage: "",
  toastVariant: "",
  sideBarOpen: true,
} as StatusContextType);

export default StatusContext;

export const StatusContextProvider = (props: { children: ReactNode }) => {
  const { children } = props;

  // リロード時にcurrentDateを復元
  const getInitialDate = () => {
    const storedDate = localStorage.getItem("currentDate");
    return storedDate ? new Date(storedDate) : new Date();
  };

  const [date, setDate] = useState(getInitialDate());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [toastTitle, setToastTitle] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [isEditing, setIsEditing] = useState(false);
  const [editingTab, setEditingTab] = useState<EditingTabInfo>();
  const [sideBarOpen, setSideBarOpen] = useState(true);
  const [lockStatus, setLockStatus] = useState<LockStatus>();

  // ---------------------------------
  // 日付の選択処理
  // ---------------------------------
  const selectDateHandler = (date: Date) => {
    setDate(date);
    localStorage.setItem("currentDate", date.toISOString());
  };

  const showToastMessage = (title: string, msg: string, variant = "success") => {
    setToastTitle(title);
    setToastMessage(msg);
    setToastVariant(variant.toLowerCase());
  };

  const setEditingTabHandler = (tabInfo: EditingTabInfo | undefined) => {
    setEditingTab(tabInfo);
    setIsEditing(tabInfo !== undefined);
  };

  const contextValue = {
    currentDate: date,
    selectDate: selectDateHandler,
    isLoading,
    setIsLoading,
    loadingMessage,
    setLoadingMessage,
    errorMessage,
    setErrorMessage,
    toastTitle,
    toastMessage,
    toastVariant,
    setToastMessage,
    showToastMessage,
    isEditing,
    setIsEditing,
    editingTab,
    setEditingTab: setEditingTabHandler,
    sideBarOpen,
    setSideBarOpen,
    lockStatus,
    setLockStatus,
  };

  return <StatusContext.Provider value={contextValue}>{children}</StatusContext.Provider>;
};
